// apps/web/src/admin/AdminIntegrityView.tsx
//
// Reports; never corrects. A drifted account links to its user drawer, where
// the adjust-with-a-reason flow lives — fixing a balance should be a deliberate
// ledger row, not a button on a dashboard.
import { useEffect, useState } from 'react';
import { apiAdmin } from './apiAdmin.js';
import { UserDetailDrawer } from './UserDetailDrawer.js';

type Check = { key: string; severity: string; violations: number; detail: any[] };

/** What each check means, in the operator's terms rather than the schema's. */
const MEANING: Record<string, { title: string; blurb: string }> = {
  balance_drift: {
    title: 'Balance drift',
    blurb: 'A cached balance disagrees with the sum of that account’s ledger rows.',
  },
  unpaid_guide: {
    title: 'Billed, no guide paid',
    blurb: 'The call was recorded, but nothing credited the guide for these minutes.',
  },
  unrecorded_session: {
    title: 'Billed, no session recorded',
    blurb: 'Minutes billed against a call the LiveKit webhook never recorded. A run of these means the webhook is down.',
  },
};

/** Severity as a word an operator recognizes, not the raw schema string. Shown
 * only on a check that currently has violations (see CheckCard) — a clean
 * check never surfaces severity, because severity describes a defect that
 * isn't present. Distinguished by weight/shade within the existing ink
 * palette rather than a new color: `error` reads as loud (dark, bold, same
 * treatment the count itself gets once non-zero), `warn` reads as quiet
 * (muted, the same text-ink-500 already used for de-emphasized text
 * elsewhere in this file) — no new hues, and never text-brand on this
 * light-surfaced card. */
const SEVERITY: Record<string, { label: string; className: string }> = {
  error: { label: 'Error', className: 'text-ink-900 dark:text-ink-100 font-semibold' },
  warn:  { label: 'Warning', className: 'text-ink-500 font-medium' },
};

/** Column headers in the operator's terms, not the schema's — this is the one
 * table in /admin that would otherwise leak raw column names. Unmapped keys
 * fall back to themselves so a future column still renders rather than
 * vanishing. */
const COLUMN_LABELS: Record<string, string> = {
  user_id: 'User',
  email: 'Email',
  cached: 'Cached balance',
  ledger: 'Ledger sum',
  drift: 'Drift',
  id: 'Event',
  session_id: 'Session',
  minutes: 'Minutes',
  created_at: 'When',
};

function DetailTable({ rows, onSelectUser }: { rows: any[]; onSelectUser: (userId: string) => void }) {
  if (rows.length === 0) return null;
  const cols = Object.keys(rows[0]);
  return (
    <table className="w-full text-xs mt-2">
      <thead className="text-left text-ink-500">
        <tr>{cols.map(c => <th key={c}>{COLUMN_LABELS[c] ?? c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const userId = r.user_id;
          const clickable = typeof userId === 'string' && userId.length > 0;
          return (
            <tr
              key={i}
              onClick={clickable ? () => onSelectUser(userId) : undefined}
              onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectUser(userId); }
              } : undefined}
              tabIndex={clickable ? 0 : undefined}
              role={clickable ? 'button' : undefined}
              className={clickable ? 'cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-500 focus-visible:ring-inset' : undefined}
            >
              {cols.map(c => <td key={c}>{r[c] == null ? '—' : String(r[c])}</td>)}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CheckCard({ check, onSelectUser }: { check: Check; onSelectUser: (userId: string) => void }) {
  const meaning = MEANING[check.key];
  const clean = check.violations === 0;
  return (
    <div className="p-4 rounded border">
      <div className="flex items-baseline justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">
            {meaning?.title ?? check.key}
            {/* Zero violations must still render clean regardless of severity —
             * severity describes a defect, and there isn't one to name here. */}
            {clean ? null : (() => {
              const sev = SEVERITY[check.severity];
              return sev ? (
                <span className={`ml-2 text-[11px] uppercase tracking-wide ${sev.className}`}>
                  {sev.label}
                </span>
              ) : null;
            })()}
          </h3>
          {meaning ? <p className="text-xs text-ink-500 mt-1">{meaning.blurb}</p> : null}
        </div>
        <div className={`text-2xl font-semibold tabular-nums ${clean ? 'text-ink-500' : 'text-ink-900 dark:text-ink-100'}`}>
          {check.violations}
        </div>
      </div>
      {clean ? null : (
        <>
          <DetailTable rows={check.detail} onSelectUser={onSelectUser} />
          {check.detail.length < check.violations ? (
            <p className="text-xs text-ink-500 mt-2">
              Showing {check.detail.length} of {check.violations}.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

export function AdminIntegrityView() {
  const [checks, setChecks] = useState<Check[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openUserId, setOpenUserId] = useState<string | null>(null);

  async function reload() {
    // Cleared up front, same as the mount effect implicitly starts clean: a
    // failed reload from the drawer must not leave a stale error sitting
    // around from some earlier failure, and a successful reload must not
    // leave the view stuck on a notice from a *previous* failed reload.
    setError(null);
    try {
      const body = await apiAdmin.integrity();
      setChecks(body.checks);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const body = await apiAdmin.integrity();
        if (active) setChecks(body.checks);
      } catch (e) {
        if (active) setError((e as Error).message);
      }
    })();
    return () => { active = false; };
  }, []);

  if (error) return <div className="notice-error">{error}</div>;
  if (!checks) return <p>Loading…</p>;
  return (
    <div className="space-y-4">
      {checks.map(c => <CheckCard key={c.key} check={c} onSelectUser={setOpenUserId} />)}
      {openUserId ? (
        <UserDetailDrawer userId={openUserId} onClose={() => setOpenUserId(null)} onChange={reload} />
      ) : null}
    </div>
  );
}
